import type { Map as MapLibreMap, StyleSpecification } from 'maplibre-gl';

import { loadGeoManifest, selfHostedPoiFromMapFeature } from './geoData';
import type { OsmPointOfInterest } from './osmClient';
import { ensurePmtilesProtocol } from './geoProtocol';

type Bounds = { north: number; south: number; east: number; west: number };
type Circle = { latitude: number; longitude: number; radiusMeters: number };
type SpatialQuery = { bounds: Bounds[]; circles: Circle[]; union: Bounds; limit: number };
type Selector = { tag: string; values?: Set<string> };
type NativeFetch = typeof window.fetch;

type PoiMapState = {
  container: HTMLDivElement;
  map: MapLibreMap;
  poisUrl: string;
  ready: Promise<void>;
};

const poiSourceId = 'doddledays-self-hosted-pois';
const poiLayerId = 'doddledays-self-hosted-poi-loader';
let poiMapState: PoiMapState | null = null;
let installed = false;

function unionBounds(parts: Bounds[]): Bounds | null {
  const [first, ...rest] = parts;
  if (!first) return null;
  return rest.reduce<Bounds>((result, bounds) => ({
    north: Math.max(result.north, bounds.north),
    south: Math.min(result.south, bounds.south),
    east: Math.max(result.east, bounds.east),
    west: Math.min(result.west, bounds.west),
  }), first);
}

function circleBounds(circle: Circle): Bounds {
  const latitudeDelta = circle.radiusMeters / 111_320;
  const cosine = Math.max(0.05, Math.abs(Math.cos(circle.latitude * Math.PI / 180)));
  const longitudeDelta = circle.radiusMeters / (111_320 * cosine);
  return {
    north: Math.min(85, circle.latitude + latitudeDelta),
    south: Math.max(-85, circle.latitude - latitudeDelta),
    east: Math.min(180, circle.longitude + longitudeDelta),
    west: Math.max(-180, circle.longitude - longitudeDelta),
  };
}

export function parseOverpassSpatialQuery(query: string): SpatialQuery | null {
  const bounds: Bounds[] = [];
  const circles: Circle[] = [];

  const bboxPattern = /nwr\(\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*\)/g;
  for (const match of query.matchAll(bboxPattern)) {
    const south = Number(match[1]);
    const west = Number(match[2]);
    const north = Number(match[3]);
    const east = Number(match[4]);
    if ([south, west, north, east].every(Number.isFinite)) bounds.push({ north, south, east, west });
  }

  const aroundPattern = /nwr\(around:\s*(\d+)\s*,\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*\)/g;
  for (const match of query.matchAll(aroundPattern)) {
    const radiusMeters = Number(match[1]);
    const latitude = Number(match[2]);
    const longitude = Number(match[3]);
    if ([radiusMeters, latitude, longitude].every(Number.isFinite)) circles.push({ latitude, longitude, radiusMeters });
  }

  const allBounds = [...bounds, ...circles.map(circleBounds)];
  const union = unionBounds(allBounds);
  if (!union) return null;

  const limits = [...query.matchAll(/out\s+center\s+(\d+)/g)].map((match) => Number(match[1])).filter(Number.isFinite);
  const limit = Math.max(1, Math.min(500, limits.length > 1 ? limits.reduce((sum, value) => sum + value, 0) : (limits[0] ?? 180)));
  return { bounds, circles, union, limit };
}

export function parseOverpassSelectors(query: string): Selector[] {
  const selectors: Selector[] = [];
  const pattern = /\["([^"]+)"(?:~"\^\(([^)]*)\)\$")?\]/g;
  for (const match of query.matchAll(pattern)) {
    const tag = match[1];
    const values = match[2]
      ? new Set(match[2].split('|').map((value) => value.trim()).filter(Boolean))
      : undefined;
    selectors.push({ tag, values });
  }
  return selectors;
}

function matchesSelectors(poi: OsmPointOfInterest, selectors: Selector[]): boolean {
  if (selectors.length === 0) return true;
  const tags = poi.tags ?? {};
  return selectors.some((selector) => {
    const value = tags[selector.tag];
    if (value === undefined) return false;
    return !selector.values || selector.values.has(value);
  });
}

function distanceMeters(point: { latitude: number; longitude: number }, target: { latitude: number; longitude: number }): number {
  const toRadians = (value: number) => value * Math.PI / 180;
  const dLat = toRadians(target.latitude - point.latitude);
  const dLon = toRadians(target.longitude - point.longitude);
  const aLat = toRadians(point.latitude);
  const bLat = toRadians(target.latitude);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(aLat) * Math.cos(bLat) * Math.sin(dLon / 2) ** 2;
  return 2 * 6_371_000 * Math.asin(Math.sqrt(h));
}

function insideSpatialQuery(poi: OsmPointOfInterest, spatial: SpatialQuery): boolean {
  const inBounds = spatial.bounds.some((bounds) => (
    poi.latitude >= bounds.south && poi.latitude <= bounds.north
    && poi.longitude >= bounds.west && poi.longitude <= bounds.east
  ));
  if (inBounds) return true;
  return spatial.circles.some((circle) => distanceMeters(poi, circle) <= circle.radiusMeters);
}

function hiddenMapContainer(): HTMLDivElement {
  const container = document.createElement('div');
  Object.assign(container.style, {
    height: '512px',
    left: '-10000px',
    opacity: '0',
    pointerEvents: 'none',
    position: 'fixed',
    top: '-10000px',
    width: '512px',
  });
  container.setAttribute('aria-hidden', 'true');
  document.body.appendChild(container);
  return container;
}

async function ensurePoiMap(poisUrl: string): Promise<MapLibreMap> {
  if (poiMapState?.poisUrl === poisUrl) {
    await poiMapState.ready;
    return poiMapState.map;
  }

  if (poiMapState) {
    poiMapState.map.remove();
    poiMapState.container.remove();
    poiMapState = null;
  }

  if (!await ensurePmtilesProtocol()) throw new Error('PMTiles protocol unavailable');
  const { Map: RuntimeMapLibreMap } = await import('maplibre-gl');

  const container = hiddenMapContainer();
  const style: StyleSpecification = {
    version: 8,
    sources: {
      [poiSourceId]: {
        type: 'vector',
        url: `pmtiles://${poisUrl}`,
        attribution: '© OpenStreetMap contributors',
      },
    },
    layers: [{
      id: poiLayerId,
      type: 'circle',
      source: poiSourceId,
      'source-layer': 'pois',
      minzoom: 11,
      paint: { 'circle-opacity': 0, 'circle-radius': 1 },
    }],
  };
  const map = new RuntimeMapLibreMap({ container, style, center: [5.1, 52.1], zoom: 11, interactive: false });
  const ready = new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('POI map load timeout')), 5_000);
    map.once('load', () => {
      window.clearTimeout(timer);
      resolve();
    });
    map.once('error', (event) => {
      if (!map.loaded()) {
        window.clearTimeout(timer);
        reject(event.error ?? new Error('POI map failed to load'));
      }
    });
  });
  poiMapState = { container, map, poisUrl, ready };
  await ready;
  return map;
}

async function waitForIdle(map: MapLibreMap): Promise<void> {
  if (map.loaded() && map.areTilesLoaded()) return;
  await new Promise<void>((resolve) => {
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      window.clearTimeout(timer);
      resolve();
    };
    const timer = window.setTimeout(finish, 4_000);
    map.once('idle', finish);
  });
}

async function querySelfHostedPois(spatial: SpatialQuery): Promise<OsmPointOfInterest[]> {
  const manifest = await loadGeoManifest();
  if (!manifest) throw new Error('Self-hosted geo manifest unavailable');
  const map = await ensurePoiMap(manifest.pois);
  map.fitBounds(
    [[spatial.union.west, spatial.union.south], [spatial.union.east, spatial.union.north]],
    { duration: 0, padding: 32, maxZoom: 15 },
  );
  await waitForIdle(map);

  const seen = new Set<string>();
  const pois: OsmPointOfInterest[] = [];
  for (const feature of map.querySourceFeatures(poiSourceId, { sourceLayer: 'pois' })) {
    const poi = selfHostedPoiFromMapFeature({
      id: feature.id,
      properties: feature.properties as Record<string, unknown>,
      geometry: feature.geometry as unknown as { type?: string; coordinates?: unknown },
    });
    if (!poi || seen.has(poi.id) || !insideSpatialQuery(poi, spatial)) continue;
    seen.add(poi.id);
    pois.push(poi);
  }
  return pois;
}

function overpassDataFromBody(body: BodyInit | null | undefined): string | null {
  if (body instanceof URLSearchParams) return body.get('data');
  if (typeof body === 'string') return new URLSearchParams(body).get('data');
  return null;
}

function numericOsmId(id: string): number {
  const match = id.match(/(\d+)$/);
  if (match) return Number(match[1]);
  let hash = 2166136261;
  for (let index = 0; index < id.length; index += 1) {
    hash ^= id.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash >>> 0);
}

async function selfHostedOverpassResponse(query: string): Promise<Response | null> {
  const spatial = parseOverpassSpatialQuery(query);
  if (!spatial) return null;
  const selectors = parseOverpassSelectors(query);
  const pois = (await querySelfHostedPois(spatial))
    .filter((poi) => matchesSelectors(poi, selectors))
    .slice(0, spatial.limit);

  return new Response(JSON.stringify({
    elements: pois.map((poi) => ({
      id: numericOsmId(poi.id),
      lat: poi.latitude,
      lon: poi.longitude,
      tags: poi.tags ?? { name: poi.name },
      type: 'node',
    })),
  }), {
    status: 200,
    headers: { 'Content-Type': 'application/json; charset=utf-8', 'X-Doddledays-Geo': 'pmtiles' },
  });
}

function requestUrl(input: Parameters<NativeFetch>[0]): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

export function installSelfHostedOverpass(): void {
  if (typeof window === 'undefined' || installed) return;
  installed = true;
  const nativeFetch = window.fetch.bind(window);

  window.fetch = (async (input: Parameters<NativeFetch>[0], init?: Parameters<NativeFetch>[1]) => {
    if (requestUrl(input) !== 'https://overpass-api.de/api/interpreter') return nativeFetch(input, init);
    const query = overpassDataFromBody(init?.body);
    if (!query) return nativeFetch(input, init);
    try {
      return (await selfHostedOverpassResponse(query)) ?? nativeFetch(input, init);
    } catch {
      return nativeFetch(input, init);
    }
  }) as NativeFetch;
}
